import React from 'react';
import { 
  ExportOutlined, 
  PlayCircleOutlined, 
  PauseCircleOutlined, 
  ReloadOutlined,
  DeleteOutlined,
  SettingOutlined,
  FileTextOutlined,
  LineChartOutlined,
  SafetyOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { ServiceAction } from '../../../types/service.type';

interface ServiceActionButtonProps {
  action: ServiceAction;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: 'default' | 'primary' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  showIcon?: boolean;
  className?: string;
}

const ACTION_CONFIG = {
  visit: { icon: ExportOutlined, label: 'Visit Site', variant: 'primary' as const },
  admin: { icon: SafetyOutlined, label: 'Admin Panel', variant: 'default' as const },
  start: { icon: PlayCircleOutlined, label: 'Start', variant: 'default' as const },
  stop: { icon: PauseCircleOutlined, label: 'Stop', variant: 'default' as const },
  restart: { icon: ReloadOutlined, label: 'Restart', variant: 'default' as const },
  delete: { icon: DeleteOutlined, label: 'Delete', variant: 'danger' as const },
  settings: { icon: SettingOutlined, label: 'Settings', variant: 'default' as const },
  logs: { icon: FileTextOutlined, label: 'Logs', variant: 'default' as const },
  metrics: { icon: LineChartOutlined, label: 'Metrics', variant: 'default' as const },
  backup: { icon: SafetyOutlined, label: 'Backup', variant: 'default' as const },
  clone: { icon: CopyOutlined, label: 'Clone', variant: 'default' as const },
};

const ServiceActionButton: React.FC<ServiceActionButtonProps> = ({
  action,
  onClick,
  disabled = false,
  loading = false,
  variant,
  size = 'md',
  label,
  showIcon = true,
  className = '',
}) => {
  const config = ACTION_CONFIG[action];
  const Icon = config.icon;
  const displayLabel = label || config.label;
  const buttonVariant = variant || config.variant;

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };

  const variantClasses = {
    default: 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700',
    primary: 'bg-blue-600 border border-blue-600 text-white hover:bg-blue-700',
    danger: 'bg-white dark:bg-gray-800 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20',
  };

  const disabledClasses = 'opacity-50 cursor-not-allowed hover:bg-current';

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        inline-flex items-center gap-2 rounded-lg font-medium
        transition-all duration-200
        ${sizeClasses[size]}
        ${disabled || loading ? disabledClasses : variantClasses[buttonVariant]}
        ${className}
      `}
    >
      {loading ? (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
      ) : (
        showIcon && <Icon className="text-base" />
      )}
      <span>{displayLabel}</span>
    </button>
  );
};

export default ServiceActionButton;